import { Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import SelectBranchPage from './pages/SelectBranchPage';
import DashboardPage from './pages/DashboardPage';
import BranchesPage from './pages/BranchesPage';
import CenterManagersPage from './pages/CenterManagersPage';
import TeachersPage from './pages/TeachersPage';
import TeacherDetailPage from './pages/TeacherDetailPage';
import ParentsPage from './pages/ParentsPage';
import StudentsPage from './pages/StudentsPage';
import StudentDetailPage from './pages/StudentDetailPage';
import StudentPromotionPage from './pages/StudentPromotionPage';
import ActivitiesPage from './pages/ActivitiesPage';
import AttendancePage from './pages/AttendancePage';
import TrackerPage from './pages/TrackerPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import NotificationsPage from './pages/NotificationsPage';
import PickupPage from './pages/PickupPage';
import ReportsPage from './pages/ReportsPage';
import CompliancePage from './pages/CompliancePage';
import SettingsPage from './pages/SettingsPage';
import StaffPage from './pages/StaffPage';
import ChatPage from './pages/ChatPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import Layout from './components/Layout';
import useAuth from './hooks/useAuth';
import ParentDetailPage from './pages/ParentDetailPage';
import ReportDetailPage from './pages/ReportDetailPage';

function App() {
  const { token, user, currentBranch } = useAuth();

  const AdminRoute = ({ element }) => {
    if (!token) return <Navigate to="/login" replace />;
    if (user?.role !== 'admin') return <Navigate to="/" replace />;
    return element;
  };

  const ManagerRoute = ({ element }) => {
    if (!token) return <Navigate to="/login" replace />;
    if (user?.role !== 'center_manager') return <Navigate to="/" replace />;
    if (!currentBranch) return <Navigate to="/select-branch" replace />;
    return element;
  };

  const AdminOrManagerRoute = ({ element }) => {
    if (!token) return <Navigate to="/login" replace />;
    if (user?.role === 'admin') return element;
    if (user?.role === 'center_manager') {
      if (!currentBranch) return <Navigate to="/select-branch" replace />;
      return element;
    }
    return <Navigate to="/" replace />;
  };

  const CommonRoute = ({ element }) => {
    if (!token) return <Navigate to="/login" replace />;
    if (user?.role === 'center_manager' && !currentBranch) {
      return <Navigate to="/select-branch" replace />;
    }
    return element;
  };

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy-policy.html" element={<PrivacyPolicyPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/select-branch" element={token && user?.role === 'center_manager' ? <SelectBranchPage /> : <Navigate to="/login" replace />} />
      
      <Route path="/" element={token ? <Layout /> : <Navigate to="/login" replace />}>
        {/* Index Redirects based on role */}
        <Route index element={user?.role === 'center_manager' || user?.role === 'admin' ? <DashboardPage /> : <Navigate to="/activities" replace />} />
        
        {/* Super Admin Only Routes */}
        <Route path="branches" element={<AdminRoute element={<BranchesPage />} />} />
        <Route path="center-managers" element={<AdminRoute element={<CenterManagersPage />} />} />
        
        {/* Super Admin & Center Manager Routes */}
        <Route path="teachers" element={<AdminOrManagerRoute element={<TeachersPage />} />} />
        <Route path="teachers/:id" element={<AdminOrManagerRoute element={<TeacherDetailPage />} />} />
        <Route path="parents" element={<AdminOrManagerRoute element={<ParentsPage />} />} />
        <Route path="parents/:id" element={<AdminOrManagerRoute element={<ParentDetailPage />} />} />
        <Route path="students" element={<AdminOrManagerRoute element={<StudentsPage />} />} />
        <Route path="students/:id" element={<AdminOrManagerRoute element={<StudentDetailPage />} />} />
        <Route path="student-promotion" element={<AdminOrManagerRoute element={<StudentPromotionPage />} />} />
        <Route path="attendance" element={<AdminOrManagerRoute element={<AttendancePage />} />} />
        <Route path="tracker" element={<AdminOrManagerRoute element={<TrackerPage />} />} />
        <Route path="chat" element={<AdminOrManagerRoute element={<ChatPage />} />} />
        <Route path="announcements" element={<AdminOrManagerRoute element={<AnnouncementsPage />} />} />
        <Route path="notifications" element={<AdminOrManagerRoute element={<NotificationsPage />} />} />
        <Route path="pickup" element={<AdminOrManagerRoute element={<PickupPage />} />} />
        <Route path="compliance" element={<AdminOrManagerRoute element={<CompliancePage />} />} />
        <Route path="settings" element={<AdminOrManagerRoute element={<SettingsPage />} />} />
        
        {/* Center Manager Only Routes */}
        <Route path="staff" element={<ManagerRoute element={<StaffPage />} />} />
        
        {/* Common Routes */}
        <Route path="activities" element={<CommonRoute element={<ActivitiesPage />} />} />
        <Route path="reports" element={<CommonRoute element={<ReportsPage />} />} />
        <Route path="reports/:id" element={<CommonRoute element={<ReportDetailPage />} />} />
      </Route>
      
      <Route path="*" element={<Navigate to={token ? '/' : '/login'} replace />} />
    </Routes>
  );
}

export default App;
